import { Controller } from "@hotwired/stimulus"
import { enter, leave } from "el-transition"

// Connects to data-controller="chart"
export default class extends Controller {
	static targets = ["data", "notification", "charticon", "chartbutton", "zoombutton", "chartsettings"]
	static values = {
		chartname: String,
	}

	connect() {
		// Chart initialisieren
		this.chart.init()
	}

	dataTargetConnected() {
		// console.log("chart#dataTargetConnected")
		// Wenn ein neues Target Div mit den Chartdaten das alte ersetzt (durch Stream oder replace)
		// muss das Chart aktualisiert werden
		this.setChartData()
		this.chart.update()

		this.hideNotification()
	}

	chartsettingsTargetConnected() {
		this.setChartSettings()
	}

	update() {
		this.chart.update()
	}

	notificationTargetConnected() {
		this.chart.showLoading()
		enter(this.notificationTarget)
	}

	// Diese Methode wird für den Wechsel zwischen den Charts verwendet. Die Icons sind die "charticon" und die Buttons die "chartbutton" Targets
	// Die charticons werden in ihrem Anzeigen getoggelt, die chartbuttons werden "aktiv" gesetzt
	changeChartSeries({ currentTarget, params }) {
		if (this.chartbuttonTargets.includes(currentTarget)) {
			this.chartbuttonTargets.forEach((e) => e.removeAttribute("data-ui"))
			currentTarget.dataset.ui = "aktiv"
		} else {
			this.charticonTargets.forEach((e) => e.classList.toggle("hidden"))
		}

		switch (params.series) {
			case "steuer":
				this.chart.updateSeriesData(this.chart.seriesSteuer(), "steuer")
				break
			case "liqui":
				this.chart.updateSeriesData(this.chart.seriesLiqui(), "liqui")
				break
			case "buschutz":
				this.chart.updateSeriesData(this.chart.seriesBuschutz(), "buschutz")
				break
			case "todesfallschutz":
				this.chart.updateSeriesData(this.chart.seriesTodesfallschutz(), "todesfallschutz")
				break
			case "kategorie":
				this.chart.updateSeriesData(this.chart.seriesKategorie(), "kategorie")
				break
			case "struktur":
				this.chart.updateSeriesData(this.chart.seriesStruktur(), "struktur")
				break
			default:
				console.error("Keine Series im Chart gefunden")
				this.charticonTargets.forEach((e) => e.classList.toggle("hidden"))
		}
	}

	// Wechsel Anzeige der Anlageklassen konsolidiert und differenziert
	changeBruttowertAnsicht({ currentTarget }) {
		if (currentTarget.checked) {
			this.showAnlageklassenKonsolidiert()
			this.chart.data.status = "konsolidiert"
		} else {
			this.showAnlageklassenDifferenziert()
			this.chart.data.status = "differenziert"
		}
	}

	// Wechsel Anzeige des Cashflows Saldo und differenziert
	changeSaldoAnsicht({ currentTarget }) {
		// .checked sind ImSoUn, die Checkbox haben, .value ist der Liquiverlauf, der eine Selectbox hat
		if (currentTarget.checked || currentTarget.value == 0) {
			this.showCashflowDifferenziert()
			this.chart.data.status = "differenziert"
		} else {
			this.showCashflowSaldo()
			this.chart.data.status = "saldo"
		}
	}

	resetZoom() {
		this.chart.data.anzeigeformat = "jahr"
		this.chart.chart.zoomOut()
		this.hideZoombutton()
	}

	showZoombutton() {
		if (this.hasZoombuttonTarget) enter(this.zoombuttonTarget)
	}

	// Private Methods

	setChartData() {
		// Object Assign ersetzt in this.chart.data nur die Werte die in this.dataTarget.dataset.chartdata enthalten sind und nicht alle Werte
		Object.assign(this.chart.data, JSON.parse(this.dataTarget.dataset.chartdata))
	}

	setChartSettings() {
		this.chart.chartsettings = JSON.parse(this.chartsettingsTarget.dataset.chartsettings)
	}

	get chart() {
		return window[this.chartnameValue]
	}

	istPlanungsansicht() {
		return this.element.id?.startsWith("planung_")
	}

	planungBerechnungLoadingAnzeigen(event) {
		if (!this.planungBerechnungStatusRelevant(event)) return

		this.showBerechnungLoading()
	}

	planungBerechnungLoadingAusblenden(event) {
		if (!this.planungBerechnungStatusRelevant(event)) return

		this.hideBerechnungLoading()
	}

	planungBerechnungStatusRelevant(event) {
		return this.istPlanungsansicht() && event.detail?.ziel === "planung"
	}

	showBerechnungLoading() {
		if (this.chart?.chart) this.chart.showLoading()
	}

	hideBerechnungLoading() {
		if (this.chart?.chart) this.chart.hideLoading()
	}

	hideNotification() {
		if (!this.hasNotificationTarget) return

		leave(this.notificationTarget).then(() => {
			this.chart.hideLoading()
			this.notificationTargets.forEach((e) => {
				e.remove()
			})
		})
	}

	hideZoombutton() {
		if (this.hasZoombuttonTarget) leave(this.zoombuttonTarget)
	}

	showAnlageklassenDifferenziert() {
		this.chart.chart.update({
			series: [
				{ id: "bruttowert", visible: false, showInLegend: false },
				{ id: "aktien", visible: true, showInLegend: true },
				{ id: "anleihen", visible: true, showInLegend: true },
				{ id: "cash", visible: true, showInLegend: true },
				{ id: "sonstiges", visible: true, showInLegend: true },
			],
		})
	}

	showAnlageklassenKonsolidiert() {
		this.chart.chart.update({
			series: [
				{ id: "bruttowert", visible: true, showInLegend: true },
				{ id: "aktien", visible: false, showInLegend: false },
				{ id: "anleihen", visible: false, showInLegend: false },
				{ id: "cash", visible: false, showInLegend: false },
				{ id: "sonstiges", visible: false, showInLegend: false },
			],
		})
	}

	showCashflowDifferenziert() {
		this.chart.chart.update({
			series: [
				{ id: "einnahmen", visible: true, showInLegend: true },
				{ id: "ausgaben", visible: true, showInLegend: true },
				{ id: "verbindlichkeiten", visible: true, showInLegend: true },
				{ id: "krediteinnahmen", visible: true, showInLegend: false },
				{ id: "kreditausgaben", visible: true, showInLegend: false },
				{ id: "saldo", visible: false, showInLegend: false },
			],
		})
	}

	showCashflowSaldo() {
		this.chart.chart.update({
			series: [
				{ id: "einnahmen", visible: false, showInLegend: false },
				{ id: "ausgaben", visible: false, showInLegend: false },
				{ id: "verbindlichkeiten", visible: true, showInLegend: true },
				{ id: "krediteinnahmen", visible: false, showInLegend: false },
				{ id: "kreditausgaben", visible: false, showInLegend: false },
				{ id: "saldo", visible: true, showInLegend: true },
			],
		})
	}
}
